import { useState, useCallback } from "react";
import { toast } from "sonner";
import { router } from "@inertiajs/react";
import {
  selectBack,
  selectCanGoBack,
  selectCanGoForward,
  selectContinue,
  selectSubstep,
  selectHasVisitedReview,
  selectReturnToReview,
  selectCampaignUUID,
  useWorkflow,
} from "@context/WorkflowProvider";
import { useCampaignId, useProjectId } from "~/stores/projectStore";
import { useAdvanceCampaign, useBackCampaign, useCampaignService } from "@components/ads/hooks";
import { useDeployService } from "@api/deploys.hooks";
import { selectValidateAndSave, selectSetServerErrors, useFormRegistry } from "@stores/formRegistry";
import type { UpdateCampaignRequestBody } from "@rails_api_base";

export function usePaginationFooter() {
  const validateAndSave = useFormRegistry(selectValidateAndSave);
  const setServerErrors = useFormRegistry(selectSetServerErrors);
  const campaignId = useCampaignId();
  const projectId = useProjectId();
  const service = useCampaignService();
  const deployService = useDeployService();
  const [validationFailed, setValidationFailed] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);

  const substep = useWorkflow(selectSubstep);
  const workflowContinue = useWorkflow(selectContinue);
  const workflowBack = useWorkflow(selectBack);
  const canGoBack = useWorkflow(selectCanGoBack);
  const canGoForward = useWorkflow(selectCanGoForward);
  const hasVisitedReview = useWorkflow(selectHasVisitedReview);
  const returnToReview = useWorkflow(selectReturnToReview);
  const campaignUuid = useWorkflow(selectCampaignUUID);

  const showPrimaryAction = hasVisitedReview && substep !== "review";

  const { mutate: advanceCampaign, isPending: isAdvancing } = useAdvanceCampaign(campaignId!);
  const { mutate: backCampaign, isPending: isGoingBack } = useBackCampaign(campaignId!);

  const clearValidationFailed = useCallback(() => {
    setValidationFailed(false);
  }, []);

  const handleContinue = async () => {
    if (!substep || !campaignId) {
      console.warn("[PaginationFooter] handleContinue aborted: substep=%s, campaignId=%s", substep, campaignId);
      return;
    }

    const result = await validateAndSave(substep, async (data) => {
      await service.update(campaignId, data as UpdateCampaignRequestBody);
    });

    if (!result.valid) {
      toast.error(
        result.error?.message || "Something doesn't look right. Please check your inputs or contact support.",
        {
          action: !result.error
            ? { label: "Get Help", onClick: () => window.location.assign("/support") }
            : undefined,
        }
      );
      setValidationFailed(true);
      return;
    }

    // review is the terminal campaign substep. "Launch Campaign" doesn't
    // advance the workflow — it explicitly creates a campaign Deploy via
    // POST /api/v1/deploys, then navigates to the deploy page (now a pure
    // render of current_deploy). No POST /advance, no workflowContinue.
    if (substep === "review") {
      if (!campaignUuid || !projectId || !campaignId) {
        console.warn(
          "[PaginationFooter] review→deploy aborted: campaignUuid=%s projectId=%s campaignId=%s",
          campaignUuid, projectId, campaignId
        );
        return;
      }
      setIsLaunching(true);
      try {
        await deployService.create({ projectId, deployType: "campaign", campaignId });
      } catch (error) {
        setIsLaunching(false);
        toast.error(error instanceof Error ? error.message : "Failed to start deploy");
        return;
      }
      // Stay in launching state through navigation — router.visit unmounts
      // this hook, so resetting after isn't needed. Spinner shows until the
      // deploy page mounts.
      router.visit(`/campaigns/${campaignUuid}/deploy`, { preserveScroll: true });
      return;
    }

    advanceCampaign(undefined, {
      onSuccess: workflowContinue,
      onError: (error) => {
        setServerErrors(substep, error);
        toast.error("Please fix the highlighted issues before continuing.");
        setValidationFailed(true);
      },
    });
  };

  const handleBack = () => {
    backCampaign(undefined, {
      onSuccess: workflowBack,
    });
  };

  return {
    handleBack,
    handleContinue,
    returnToReview,
    canGoBack,
    canGoForward,
    isPending: isAdvancing || isGoingBack || isLaunching,
    showPrimaryAction,
    validationFailed,
    clearValidationFailed,
  };
}
